<script setup lang="ts">
import { computed } from 'vue';

import { useAlertStore } from '../store/alert-store';

const alertStore = useAlertStore();

const orderedAlerts = computed(() => [...alertStore.alerts].reverse());

const closeAll = () => {
  alertStore.hideAllAlerts();
};

type vAlertTypes = 'success' | 'info' | 'warning' | 'error';

const cleanAlertType = (type: string): vAlertTypes => {
  // If the type is 'progress', we treat it as 'info' for styling purposes
  // Otherwise, we return the type as is, ensuring it matches one of the vAlertTypes
  const validTypes: vAlertTypes[] = ['success', 'info', 'warning', 'error'];
  return validTypes.includes(type as vAlertTypes)
    ? (type as vAlertTypes)
    : 'info';
};

const stackStyle = (index: number) => {
  const translateY = -index * 8;
  const translateZ = -index * 18;
  const scale = Math.max(1 - index * 0.03, 0.9);
  // If we have more than 4, opacity will be 0
  const maxVisible = 4;

  const opacity = index < maxVisible ? Math.max(1 - index * 0.12, 0.2) : 0;

  return {
    '--stack-translate-y': `${translateY}px`,
    '--stack-translate-z': `${translateZ}px`,
    '--stack-scale': `${scale}`,
    '--stack-opacity': `${opacity}`,
    'zIndex': `${100 - index}`,
    'pointerEvents': index === 0 ? 'auto' : 'none',
  } as Record<string, string>;
};

defineExpose({
  name: 'AlertBanner',
});
</script>

<template>
  <div class="alert-container">
    <div v-if="alertStore.alerts.length > 1" class="close-all-container">
      <button class="close-all-btn" @click="closeAll">
        <span class="close-all-icon">✕</span>
        Close All ({{ alertStore.alerts.length }})
      </button>
    </div>
    <TransitionGroup name="alert-transition" tag="div" class="alert-stack">
      <div
        v-for="(alert, index) in orderedAlerts"
        :key="alert.id"
        class="alert-wrapper"
        :style="stackStyle(index)"
      >
        <div class="inner-container">
          <v-alert
            :closable="!alert.buttons?.length"
            :type="cleanAlertType(alert.type)"
            variant="tonal"
            density="default"
            class="pa-4"
            @click:close="alertStore.hideAlert(alert.id)"
          >
            <div class="alert-content-container">
              <div class="alert-message-text">
                {{ alert.content }}
              </div>

              <!-- Button row for interactive alerts -->
              <div v-if="alert.buttons?.length" class="alert-button-row">
                <button
                  v-for="btn in alert.buttons"
                  :key="btn.value"
                  class="alert-action-btn"
                  :class="`alert-action-btn--${btn.variant || 'secondary'}`"
                  @click.stop="alertStore.resolveAlert(alert.id, btn.value)"
                >
                  {{ btn.label }}
                </button>
              </div>
            </div>

            <!-- progress and keepAlive will both have a progress bar -->
            <template
              v-if="
                !alert.buttons?.length
                  && (!alert.keepAlive || alert.type === 'progress')
              "
            >
              <div class="progress-container">
                <div
                  class="progress-bar"
                  :style="{ width: `${alert.progress}%` }"
                  :class="alert.type"
                />
              </div>
            </template>
          </v-alert>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.alert-container {
  position: fixed;
  z-index: 999999;
  bottom: 12px;
  right: 12px;
  width: 450px;
  max-width: 90vw;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 10px;
  justify-items: end;
  pointer-events: none;
  perspective: 900px;
}

.alert-stack {
  position: relative;
  display: grid;
  width: 100%;
  justify-items: end;
  align-items: end;
}

.alert-wrapper {
  width: 100%;
  grid-area: 1 / 1;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 3px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transform-style: preserve-3d;
  opacity: var(--stack-opacity, 1);
  transform: translateY(var(--stack-translate-y, 0px)) translateZ(var(--stack-translate-z, 0px))
    scale(var(--stack-scale, 1));
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
  pointer-events: auto;
}

.inner-container {
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: var(--md-bg);
  position: relative;
  max-height: 300px;
  overflow: hidden;
}

.progress-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  overflow: hidden;
  border-radius: 0 0 5px 5px;
}

.progress-bar {
  height: 100%;
  transition: width 0.05s linear;
}

.progress-bar.success {
  background-color: var(--md-success);
}

.progress-bar.info {
  background-color: var(--md-info);
}

.progress-bar.warning {
  background-color: var(--md-warning);
}

.progress-bar.error {
  background-color: var(--md-error);
}

.progress-bar.progress {
  background-color: var(--md-info);
}

.progress-bar.progress {
  background-color: var(--md-primary);
}

.v-alert {
  white-space: pre-line;
}

.inner-container :deep(.v-alert) {
  white-space: pre-line;
  margin-bottom: 0;
  padding: 12px 16px !important;
  max-height: 300px;
  box-sizing: border-box;
  align-items: flex-start;
}

.inner-container :deep(.v-alert__content) {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(300px - 24px);
  overflow: hidden;
}

.inner-container :deep(.v-alert__prepend) {
  margin-inline-end: 12px !important;
  align-self: flex-start;
}

.inner-container :deep(.v-alert__append) {
  margin-inline-start: 12px !important;
}

.inner-container :deep(.v-alert__close) {
  margin-inline-start: 12px !important;
  align-self: flex-start;
}

.alert-content-container {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  width: 100%;
}

.alert-message-text {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;
  user-select: text;
  font-size: 14px;
  line-height: 1.45;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: var(--md-outline) transparent;
}

.alert-message-text::-webkit-scrollbar {
  width: 4px;
}

.alert-message-text::-webkit-scrollbar-track {
  background: transparent;
}

.alert-message-text::-webkit-scrollbar-thumb {
  background: var(--md-outline);
  border-radius: 4px;
}

.alert-message-text::-webkit-scrollbar-thumb:hover {
  background: var(--md-on-surface-variant);
}

/* Animation for alert stack */
.alert-transition-enter-active,
.alert-transition-leave-active {
  transition: all 0.25s ease;
}

.alert-transition-move {
  transition: transform 0.3s ease;
}

.alert-transition-enter-from {
  opacity: 0;
  transform: translateY(calc(var(--stack-translate-y, 0px) + 24px)) translateZ(var(--stack-translate-z, 0px))
    scale(var(--stack-scale, 1));
}

.alert-transition-leave-to {
  opacity: 0;
  transform: translateX(40px) translateY(var(--stack-translate-y, 0px)) translateZ(var(--stack-translate-z, 0px))
    scale(var(--stack-scale, 1));
}

.alert-transition-leave-active {
  position: absolute;
  right: 0;
}

.close-all-container {
  display: flex;
  justify-content: flex-end;
  pointer-events: auto;
}

.close-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 16px;
  background: var(--md-surface-variant);
  color: var(--md-on-surface-variant);
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.2s ease;
}

.close-all-btn:hover {
  background: var(--md-surface);
  transform: translateY(-1px);
}

.close-all-btn:active {
  transform: translateY(0);
}

.close-all-icon {
  font-size: 12px;
  font-weight: bold;
}

/* ── Alert action buttons ─────────────────────────────────── */
.alert-button-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  /* Set to most right */
  justify-content: flex-end;
  margin-top: 12px;
}

.alert-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  user-select: none;
}

.alert-action-btn:active {
  transform: scale(0.96);
}

/* ─ Primary: filled accent */
.alert-action-btn--primary {
  background: var(--md-primary);
  color: var(--md-on-primary);
}
.alert-action-btn--primary:hover {
  background: var(--md-primary-container);
}

/* ─ Secondary: outlined */
.alert-action-btn--secondary {
  background: transparent;
  color: var(--md-on-secondary);
  border: 1px solid var(--md-on-secondary);
}

/* ─ Danger: filled destructive */
.alert-action-btn--danger {
  background: var(--md-tertiary);
  color: var(--md-on-tertiary);
}
.alert-action-btn--danger:hover {
  background: var(--md-tertiary-container);
}

/* ─ Ghost: text-only, minimal */
.alert-action-btn--ghost {
  background: transparent;
  color: var(--md-on-secondary);
}
</style>
